import { resolve } from 'node:path'
import { Command } from 'commander'
import { loadConfig } from '../config.js'
import { loadKey } from '../crypto/keys.js'
import { verifyEnvelope } from '../crypto/dsse.js'
import { generateSealingKeyPair } from '../crypto/seal.js'
import { findManifest, loadManifest } from '../vpkg/manifest.js'
import { pack, type PackRecipient } from '../vpkg/pack.js'
import { readVpkg } from '../vpkg/read.js'

export function registerPack(program: Command): void {
  program
    .command('pack [dir]')
    .description('Build a signed .vpkg from a project directory')
    .option('--manifest <path>', 'manifest path (default: auto-detected in the project directory)')
    .option('--publisher <name>', 'publisher name (default: packaging.publisher in the manifest)')
    .option('--name <name>', 'override the package name')
    .option('--out <dir>', 'output directory (default: the project directory)')
    .option('--key <name>', 'signing key name (default: the configured default key)')
    .option('--no-sign', 'produce an unsigned package')
    .option('--encrypt', 'seal the payload (AES-256-GCM); requires at least one recipient')
    .option('--recipient <id=base64key...>', 'additional X25519 recipient (repeatable)', collectRecipients, [] as PackRecipient[])
    .action(async (dir: string | undefined, options: { manifest?: string; publisher?: string; name?: string; out?: string; key?: string; sign: boolean; encrypt?: boolean; recipient: PackRecipient[] }) => {
      const projectDir = resolve(dir ?? '.')
      const manifestPath = options.manifest ? resolve(options.manifest) : await findManifest(projectDir)
      const manifest = await loadManifest(manifestPath)

      const publisher = options.publisher ?? manifest.packaging?.publisher
      if (!publisher) {
        throw new Error('No publisher: set packaging.publisher in the manifest or pass --publisher.')
      }

      const config = await loadConfig()

      let signingKey
      if (options.sign) {
        const keyName = options.key ?? config.defaultKey
        if (keyName) {
          signingKey = await loadKey(keyName)
        } else {
          console.warn('Warning: no signing key configured — producing an UNSIGNED package. Run `verentis keygen` to create one.')
        }
      }

      let encryptFor: PackRecipient[] | undefined
      if (options.encrypt || manifest.packaging?.encryption === 'required') {
        encryptFor = [...options.recipient]
        const platformKey = process.env.VERENTIS_SEALING_KEY ?? config.sealingPublicKey
        if (platformKey && !encryptFor.some((r) => r.id === 'platform')) {
          // The platform recipient lets Verentis.Node serve member reads from the sealed package.
          encryptFor.unshift({ id: 'platform', publicKey: platformKey })
        }
        if (encryptFor.length === 0) {
          throw new Error(
            'Encryption requires at least one recipient. Configure the platform sealing key '
              + '(`verentis login --sealing-key <base64>` / VERENTIS_SEALING_KEY) or pass --recipient id=key.',
          )
        }
      }

      const result = await pack({
        manifestPath,
        publisher,
        name: options.name,
        outDir: options.out,
        signingKey,
        encryptFor,
      })

      console.log(`Packed ${result.publisher}/${result.name}@${result.version} (${result.kind})`)
      console.log(`  ${result.outFile}`)
      console.log(`  ${result.files.length} payload file(s), tree digest ${result.treeDigest.slice(0, 16)}…, ${result.signed ? 'signed' : 'UNSIGNED'}${result.encrypted ? ', ENCRYPTED' : ''}`)
    })

  program
    .command('seal-keygen')
    .description('Generate an X25519 sealing keypair (for platform escrow or custom recipients)')
    .action(() => {
      const pair = generateSealingKeyPair()
      console.log(`public:  ${pair.publicKey}`)
      console.log(`private: ${pair.privateKey}`)
      console.log('Distribute the PUBLIC key to packers; keep the private key in the consumer (e.g. Node Packaging:SealingPrivateKey).')
    })

  program
    .command('verify <vpkg>')
    .description('Verify the digests (and, when possible, the developer signature) of a .vpkg')
    .option('--key <name>', 'verify the developer signature against a local key')
    .action(async (vpkgPath: string, options: { key?: string }) => {
      const contents = await readVpkg(resolve(vpkgPath))
      console.log(`${contents.metadata.publisher}/${contents.metadata.name}@${contents.metadata.version} (${contents.metadata.kind})`)
      console.log(`  files: ${contents.files.length}, tree digest ${contents.treeDigest.slice(0, 16)}…`)
      if (contents.encryption) {
        console.log(`  🔒 encrypted (${contents.encryption.algorithm}); recipients: ${contents.encryption.recipients.map((r) => r.id).join(', ')}`)
      }

      if (contents.digestErrors.length > 0) {
        for (const error of contents.digestErrors) console.error(`  ✗ ${error}`)
        process.exitCode = 1
      } else {
        console.log('  ✓ digests verified')
      }

      if (contents.developerSignature) {
        if (options.key) {
          const key = await loadKey(options.key)
          const kid = verifyEnvelope(contents.developerSignature, contents.digestsJson, { [key.kid]: key.publicKey })
          if (kid) {
            console.log(`  ✓ developer signature verified (kid ${kid})`)
          } else {
            console.error('  ✗ developer signature does NOT verify with that key')
            process.exitCode = 1
          }
        } else {
          console.log(`  • developer signature present (keyid ${contents.developerSignature.signatures[0]?.keyid ?? '?'}) — pass --key to verify locally`)
        }
      } else {
        console.log('  • unsigned')
      }

      if (contents.marketplaceSignature) console.log('  ✓ marketplace countersignature present')
    })
}

function collectRecipients(value: string, previous: PackRecipient[]): PackRecipient[] {
  const eq = value.indexOf('=')
  if (eq <= 0) throw new Error(`Invalid --recipient '${value}' — expected id=base64PublicKey.`)
  return [...previous, { id: value.slice(0, eq), publicKey: value.slice(eq + 1) }]
}
